import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@material-ui/core";
import styled from "styled-components";

import { FlexBox } from "js/react/components/LayoutGrid";
import { TextContent } from "js/Components/TextContent";
import { Divider } from "js/Components/Divider";
import type { TeamCheckoutFlow } from "js/react/views/Billing/Checkout/plans/team/TeamCheckoutFlow";
import { Button } from "js/Components/Button";
import { themeColors } from "js/react/sharedStyles";

import { AddressForm, type MinimalBillingAddress } from "../../../components/AddressForm/AddressForm";
import { PaymentForm } from "../../../components/PaymentForm/PaymentForm";
import { PlanFeatures } from "../../../components/PlanFeatures/PlanFeatures";
import { PromoCoupon, PromoForm } from "../../../components/PromoForm/PromoForm";
import { SeatsSelect } from "../../../components/SeatsSelect/SeatsSelect";
import { TermsOfUseAndRefundPolicy } from "../../../components/TermsOfUseAndRefundPolicy/TermsOfUseAndRefundPolicy";
import { formatPrice } from "../../../utils/prices";

import {
    CheckoutContentScroll,
    CheckoutSwitchLink,
    ErrorMessageContainer,
    LeftColumn,
    LeftForm,
    LeftFooter,
    PromoContainer,
    SeatsContainer,
    SummaryBlock,
    SummaryCard,
    SummaryContent,
    SummaryRow,
    SummarySection,
    SummaryTitle,
    TeamPlanInternal,
} from "./TeamCheckoutStyles";

const MAX_SEATS = 20;

const SubmitButton = styled(Button) <{ fullWidth?: boolean }>`
${props => props.fullWidth && `
    width: 100%;
`}
`;

const CancelButton = styled(Button)`
    &&& {
        background: ${themeColors.lightGray};
        color: ${themeColors.mediumGray};
        border:none;
    }
`;

export interface TeamCheckoutDesktopLayoutProps {
    checkoutFlow: TeamCheckoutFlow;
    title: string;
    buttonLabel: string;
    error: string | null;
    seatCount: number;
    pricePerSeat: number;
    subtotal: number;
    taxTotal: number;
    dueNext: number;
    isAnnual: boolean;
    availableTrialDays: number;
    trialExpirationDate: string;
    annualSavingsPercentage: number;
    promo: { coupon: { amount_off?: number; percent_off?: number } } | null;
    billingAddress: MinimalBillingAddress;
    paymentMethodCreationError: string | null;
    prices: { year: { amount: number }; month?: { amount: number } };
    onPaymentElementChange: (event: unknown) => void;
    onBillingAddressChange: ({ address, isAddressValid }: { address: MinimalBillingAddress; isAddressValid: boolean }) => void;
    onSeatCountChange: (seatCount: number) => void;
    onContactSales: () => void;
    onVerifyPromoCode: (promo: unknown) => Promise<void>;
    onBillingIntervalChange: (billingInterval: "year" | "month") => void;
    onSubmit: () => void;
    canStartTrial: boolean;
    onCancel: () => void;
}

export function TeamCheckoutDesktopLayout({
    checkoutFlow,
    title,
    onCancel,
    buttonLabel,
    error,
    seatCount,
    pricePerSeat,
    subtotal,
    taxTotal,
    dueNext,
    isAnnual,
    availableTrialDays,
    trialExpirationDate,
    annualSavingsPercentage,
    promo,
    billingAddress,
    paymentMethodCreationError,
    prices,
    onPaymentElementChange,
    onBillingAddressChange,
    onSeatCountChange,
    onContactSales,
    onVerifyPromoCode,
    onBillingIntervalChange,
    onSubmit,
    canStartTrial,
}: TeamCheckoutDesktopLayoutProps) {
    const { t } = useTranslation();
    const showBillingIntervalSwitch = prices.month && checkoutFlow !== "reactivate";
    const showContactSupport = checkoutFlow === "reactivate";
    const isTrialFlow = checkoutFlow === "new_with_trial";
    const dueTodayAmount = isTrialFlow ? 0 : dueNext;
    const showDueTodayTax = !isTrialFlow && taxTotal > 0;
    const showTrialEndRow = isTrialFlow && availableTrialDays > 0;

    return (
        <CheckoutContentScroll>
            <FlexBox row verticalAlign="start" gap={20} height="100%">
                <LeftColumn>
                    <TextContent dialogTitle>{title}</TextContent>
                    <LeftForm>
                        <PaymentForm
                            variant="B"
                            paymentMethodCreationError={paymentMethodCreationError}
                            handlePaymentElementChange={onPaymentElementChange}
                        />
                        <AddressForm
                            variant="B"
                            initialAddress={billingAddress}
                            onChange={onBillingAddressChange}
                        />
                        {error && <ErrorMessageContainer>{error}</ErrorMessageContainer>}
                        <TextContent disclaimer>
                            {t("By providing your card information, you allow Beautiful.ai to charge your card for future payments in accordance with their terms.")}
                        </TextContent>
                    </LeftForm>
                    <LeftFooter>
                        <FlexBox row verticalAlign="center" horizontalAlign="right" gap={10}>
                            {onCancel && (
                                <CancelButton
                                    data-test-id="payment-form-cancel-button"
                                    onClick={onCancel}
                                    unframed
                                >
                                    {t("Cancel")}
                                </CancelButton>
                            )}
                            <SubmitButton
                                fullWidth={!onCancel}
                                data-test-id="payment-form-submit-button"
                                onClick={onSubmit}
                                disabled={!canStartTrial}
                                blue
                                large
                                onKeyDown={(e: React.KeyboardEvent) => {
                                    if (!canStartTrial && (e.key === "Enter" || e.key === " ")) {
                                        e.preventDefault();
                                        onSubmit();
                                    }
                                }}
                            >
                                {buttonLabel}
                            </SubmitButton>
                        </FlexBox>

                        <TextContent disclaimer center>
                            {t("By continuing you agree to our")}&nbsp;
                            <TermsOfUseAndRefundPolicy size="small" />
                        </TextContent>
                        {showBillingIntervalSwitch && (
                            <TextContent disclaimer center>
                                {isAnnual && (
                                    <>
                                        Pay {formatPrice(prices.year.amount / 12 / 100)}/mo (
                                        {formatPrice(prices.year.amount / 100)}/yr) when you{" "}
                                        <CheckoutSwitchLink onClick={() => onBillingIntervalChange("month")}>
                                            Switch to monthly billing
                                        </CheckoutSwitchLink>
                                        .
                                    </>
                                )}
                                {!isAnnual && (
                                    <>
                                        {t("Save {{savingsPercentage}}% when you", {
                                            savingsPercentage: annualSavingsPercentage.toFixed(0),
                                        })}{" "}
                                        <CheckoutSwitchLink onClick={() => onBillingIntervalChange("year")}>
                                            {t("switch to annual billing")}
                                        </CheckoutSwitchLink>
                                        .
                                    </>
                                )}
                            </TextContent>
                        )}
                        {showContactSupport && (
                            <TextContent disclaimer center>
                                <Link
                                    href="mailto:support@beautiful.ai"
                                >{t("Contact support")}</Link> {t("for further assistance.")}
                            </TextContent>
                        )}
                    </LeftFooter>
                </LeftColumn>

                <FlexBox column gap={10} horizontalAlign="start" fillHeight>
                    <SummaryCard>
                        <SummaryContent>
                            <SummaryTitle>{t("Summary")}</SummaryTitle>
                            <SummaryBlock>
                                <SummaryRow>
                                    <TeamPlanInternal helper>
                                        {t("Team Plan - {{billingInterval}} billing", {
                                            billingInterval: isAnnual ? t("Annual") : t("Monthly"),
                                        })}
                                    </TeamPlanInternal>
                                </SummaryRow>
                                <Divider margin={15} />
                                <SeatsContainer>
                                    <TeamPlanInternal>Seats</TeamPlanInternal>
                                    <SeatsSelect
                                        disabled={checkoutFlow === "reactivate"}
                                        seatCount={seatCount}
                                        maxSeats={MAX_SEATS}
                                        onSeatCountChange={onSeatCountChange}
                                        onContactSales={onContactSales}
                                    />
                                </SeatsContainer>
                                <Divider margin={15} />
                                <SummarySection>
                                    <SummaryRow>
                                        <TeamPlanInternal helper>
                                            {formatPrice(pricePerSeat)} x {seatCount} seats
                                        </TeamPlanInternal>
                                        <TeamPlanInternal helper>{formatPrice(subtotal)}</TeamPlanInternal>
                                    </SummaryRow>
                                    <SummaryRow>
                                        <TeamPlanInternal helper>{t("Tax")}</TeamPlanInternal>
                                        <TeamPlanInternal helper>
                                            {taxTotal > 0 ? `+ ${formatPrice(taxTotal)}` : "—"}
                                        </TeamPlanInternal>
                                    </SummaryRow>
                                </SummarySection>
                                <Divider margin={15} />
                                <SummarySection>
                                    <SummaryRow>
                                        <TextContent sectionLabel style={{ color: "#222" }}>
                                            {t("Due today")}
                                        </TextContent>
                                        <TextContent sectionLabel style={{ color: "#222" }}>
                                            {formatPrice(dueTodayAmount)}
                                            {showDueTodayTax ? ` ${t("plus tax")}` : ""}
                                        </TextContent>
                                    </SummaryRow>
                                    {showTrialEndRow && (
                                        <SummaryRow>
                                            <TeamPlanInternal helper>
                                                {t("Due at the end of the {{period}} on {{date}}", {
                                                    period: t("trial period"),
                                                    date: trialExpirationDate,
                                                })}
                                            </TeamPlanInternal>
                                            <TeamPlanInternal helper>
                                                {formatPrice(dueNext)}
                                                {taxTotal > 0 ? ` ${t("plus tax")}` : ""}
                                            </TeamPlanInternal>
                                        </SummaryRow>
                                    )}
                                </SummarySection>
                            </SummaryBlock>
                            <PromoContainer>
                                {!promo ? (
                                    <PromoForm variant="B" verifyPromoCode={onVerifyPromoCode} />
                                ) : (
                                    <PromoCoupon variant="B" coupon={promo.coupon} />
                                )}
                            </PromoContainer>
                        </SummaryContent>
                    </SummaryCard>
                    <PlanFeatures isAnnual={isAnnual} isTrial={isTrialFlow} />
                </FlexBox>
            </FlexBox>
        </CheckoutContentScroll>
    );
}
